import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.username || 'John Doe';

  const initials =
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
      : user?.username
      ? user.username.slice(0, 2).toUpperCase()
      : 'JD';

  const navItems = [
    { id: 'overview', icon: 'dashboard', label: 'Overview' },
    { id: 'projects', icon: 'account_tree', label: 'My Projects' },
    { id: 'agents', icon: 'smart_toy', label: 'AI Agents' },
    { id: 'analytics', icon: 'analytics', label: 'Analytics' },
    { id: 'settings', icon: 'settings', label: 'Settings' },
  ];

  const stats = [
    {
      label: 'Active Projects',
      value: '3',
      icon: 'folder',
      gradient: 'linear-gradient(135deg, #13a4ec, #0e7fc0)',
      glow: 'rgba(19,164,236,0.3)',
    },
    {
      label: 'AI Agents',
      value: '6',
      icon: 'smart_toy',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      glow: 'rgba(16,185,129,0.3)',
    },
    {
      label: 'Tasks Completed',
      value: '247',
      icon: 'task_alt',
      gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
      glow: 'rgba(124,58,237,0.3)',
    },
    {
      label: 'Success Rate',
      value: '98.2%',
      icon: 'trending_up',
      gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
      glow: 'rgba(249,115,22,0.3)',
    },
  ];

  const projects = [
    {
      id: 1,
      name: 'Customer Support Automation',
      status: 'Active',
      progress: 85,
      agents: 3,
      updated: '2 hours ago',
    },
    {
      id: 2,
      name: 'Sales Lead Processing',
      status: 'In Progress',
      progress: 60,
      agents: 2,
      updated: '1 day ago',
    },
    {
      id: 3,
      name: 'HR Onboarding Flow',
      status: 'Completed',
      progress: 100,
      agents: 1,
      updated: '3 days ago',
    },
  ];

  const activity = [
    {
      id: 1,
      icon: 'check_circle',
      color: '#10b981',
      message: 'Customer Support workflow processed 45 tickets',
      time: '10 minutes ago',
    },
    {
      id: 2,
      icon: 'rocket_launch',
      color: '#13a4ec',
      message: 'Sales Assistant agent deployed to production',
      time: '2 hours ago',
    },
    {
      id: 3,
      icon: 'build',
      color: '#f97316',
      message: 'API timeout issue resolved for DevOps Bot',
      time: '4 hours ago',
    },
    {
      id: 4,
      icon: 'integration_instructions',
      color: '#7c3aed',
      message: 'Slack integration added to HR Automator',
      time: '1 day ago',
    },
  ];

  const quickActions = [
    {
      icon: 'add_circle',
      title: 'Create Workflow',
      desc: 'Build a new automation pipeline',
      to: '/workflow-builder',
      gradient: 'linear-gradient(135deg, #13a4ec, #0e7fc0)',
    },
    {
      icon: 'smart_toy',
      title: 'Deploy Agent',
      desc: 'Launch a new AI agent',
      to: '/ai-agents',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
    },
    {
      icon: 'analytics',
      title: 'View Analytics',
      desc: 'Explore logs and insights',
      to: '/logs',
      gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
    },
    {
      icon: 'manage_accounts',
      title: 'Manage Settings',
      desc: 'Update your preferences',
      to: '/account',
      gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
    },
  ];

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return { color: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' };
      case 'In Progress':
        return { color: '#13a4ec', backgroundColor: 'rgba(19,164,236,0.1)', border: '1px solid rgba(19,164,236,0.2)' };
      case 'Completed':
        return { color: '#9ca3af', backgroundColor: 'rgba(156,163,175,0.1)', border: '1px solid rgba(156,163,175,0.2)' };
      default:
        return { color: '#9ca3af', backgroundColor: 'rgba(156,163,175,0.1)', border: '1px solid rgba(156,163,175,0.2)' };
    }
  };

  return (
    <div
      className="min-h-screen flex font-display"
      style={{ backgroundColor: '#101c22', color: '#fff' }}
    >
      {/* ── Sidebar ── */}
      <aside
        className="w-64 flex-shrink-0 flex flex-col border-r border-white/10 hidden lg:flex"
        style={{ backgroundColor: '#0d1820' }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8" style={{ color: '#13a4ec' }}>
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4" />
                <circle cx="24" cy="24" fill="currentColor" r="8" />
                <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
              </svg>
            </div>
            <span className="text-lg font-bold text-white">OrbitMind</span>
          </div>
          <p className="text-xs text-gray-500 pl-11">Personal Dashboard</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all"
              style={
                activeTab === item.id
                  ? {
                      background: 'linear-gradient(135deg, rgba(19,164,236,0.15), rgba(124,58,237,0.15))',
                      color: '#13a4ec',
                      border: '1px solid rgba(19,164,236,0.2)',
                    }
                  : { color: '#9ca3af', border: '1px solid transparent' }
              }
            >
              <span className="material-symbols-outlined text-xl">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* Upgrade CTA */}
        <div className="p-4 border-t border-white/10">
          <button
            className="w-full py-3 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
          >
            <span className="material-symbols-outlined text-base">workspace_premium</span>
            Upgrade to Pro
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top Header */}
        <header
          className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0"
          style={{ backgroundColor: '#0d1820' }}
        >
          {/* Search */}
          <div className="relative w-72">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl pointer-events-none">
              search
            </span>
            <input
              type="text"
              placeholder="Search projects, agents..."
              className="w-full h-10 pl-10 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm"
            />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Notification bell */}
            <button className="relative w-10 h-10 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all">
              <span className="material-symbols-outlined text-gray-400 text-xl">notifications</span>
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500" />
            </button>

            {/* User */}
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
              >
                {initials}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-white leading-tight">{displayName}</p>
                <p className="text-xs text-gray-500 leading-tight">{user?.email || 'user@orbitmind.ai'}</p>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center hover:bg-red-500/10 hover:border-red-500/20 transition-all"
              title="Sign out"
            >
              <span className="material-symbols-outlined text-gray-400 text-xl">logout</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">

          {/* Welcome */}
          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-white mb-1">
              Welcome back, {user?.firstName || user?.username || 'there'}!
            </h1>
            <p className="text-gray-400 text-sm">
              Here's what's happening with your AI automation projects today.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-white/10 p-6 transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: stat.gradient, boxShadow: `0 0 16px ${stat.glow}` }}
                  >
                    <span className="material-symbols-outlined text-white text-xl">{stat.icon}</span>
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Projects + Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

            {/* Active Projects (2/3) */}
            <div
              className="lg:col-span-2 rounded-2xl border border-white/10"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <h2 className="font-bold text-white">Active Projects</h2>
                <Link
                  to="/projects"
                  className="text-sm font-medium hover:opacity-80 transition-opacity"
                  style={{ color: '#13a4ec' }}
                >
                  View All
                </Link>
              </div>
              <div className="p-6 space-y-4">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="rounded-xl border border-white/10 p-4 hover:border-white/20 transition-all"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <h3 className="font-semibold text-white text-sm truncate">{project.name}</h3>
                        <span
                          className="px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                          style={getStatusStyle(project.status)}
                        >
                          {project.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-400 text-xs flex-shrink-0 ml-3">
                        <span className="material-symbols-outlined text-sm">smart_toy</span>
                        {project.agents}
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-gray-400">Progress</span>
                        <span className="text-white font-medium">{project.progress}%</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-white/10">
                        <div
                          className="h-1.5 rounded-full transition-all duration-500"
                          style={{
                            width: `${project.progress}%`,
                            background: 'linear-gradient(90deg, #13a4ec, #7c3aed)',
                          }}
                        />
                      </div>
                    </div>

                    <p className="text-xs text-gray-500">Last updated {project.updated}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity (1/3) */}
            <div
              className="rounded-2xl border border-white/10"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
            >
              <div className="px-6 py-5 border-b border-white/10">
                <h2 className="font-bold text-white">Recent Activity</h2>
              </div>
              <div className="p-6 space-y-5">
                {activity.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${item.color}20` }}
                    >
                      <span
                        className="material-symbols-outlined text-sm"
                        style={{ color: item.color }}
                      >
                        {item.icon}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300 leading-snug">{item.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{item.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="font-bold text-white mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <Link
                  key={action.title}
                  to={action.to}
                  className="rounded-xl border border-white/10 p-5 flex items-start gap-4 hover:border-white/20 hover:-translate-y-0.5 transition-all group"
                  style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: action.gradient }}
                  >
                    <span className="material-symbols-outlined text-white text-base">{action.icon}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm mb-0.5 group-hover:text-primary transition-colors">
                      {action.title}
                    </p>
                    <p className="text-xs text-gray-400">{action.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserDashboard;
